"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocale } from "next-intl";
import { ChevronDown, Mail, Phone } from "lucide-react";
import { Link } from "@/i18n/routing";
import type { Locale } from "@/i18n/routing";
import { company, hotlines, type LocaleKey } from "@/content/global";
import type { SectionsContent } from "@/content/sections";
import { t } from "@/lib/content";
import { cn } from "@/lib/cn";
import { BookingLink } from "@/components/ui/booking-link";
import { LanguageSwitcher } from "./language-switcher";

export function MobileNav({
  commonCopy,
  footerNav,
  mobileMenuLabels,
}: {
  commonCopy: SectionsContent["commonCopy"];
  footerNav: SectionsContent["footerNav"];
  mobileMenuLabels: SectionsContent["mobileMenuLabels"];
}) {
  const locale = useLocale() as Locale;
  const line = hotlines[locale as LocaleKey] ?? hotlines.de;

  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState<string | null>("services");

  // Mount the drawer DOM only when first opened, then keep it mounted so the
  // exit animation can play.
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  // Body scroll lock + ESC handler.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const groups: { key: string; data: typeof footerNav.servicesGroup }[] = [
    { key: "services", data: footerNav.servicesGroup },
    { key: "industries", data: footerNav.industriesGroup },
    { key: "company", data: footerNav.companyGroup },
  ];

  return (
    <>
      <button
        type="button"
        aria-label={
          open
            ? (t(mobileMenuLabels.closeMenu, locale) as string)
            : (t(mobileMenuLabels.openMenu, locale) as string)
        }
        aria-expanded={open}
        aria-controls="mobile-nav-drawer"
        onClick={() => setOpen((v) => !v)}
        className="relative lg:hidden grid h-11 w-11 place-items-center rounded-md text-ink-900 transition-colors hover:bg-soft"
      >
        <BurgerIcon open={open} />
      </button>

      {mounted && typeof document !== "undefined"
        ? createPortal(
        <div
          id="mobile-nav-drawer"
          className="fixed inset-x-0 bottom-0 top-16 z-[60] md:top-[6.5rem] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label={t(mobileMenuLabels.openMenu, locale) as string}
          onTransitionEnd={() => {
            if (!open) setMounted(false);
          }}
        >
          {/* drawer fills the area below the header */}
          <div
            className={cn(
              "flex h-full w-full flex-col overflow-hidden border-t border-line-200 bg-canvas shadow-[0_24px_48px_-16px_rgba(11,18,32,0.16)]",
              "transition-[transform,opacity] duration-300 ease-out",
              open ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
            )}
          >
            {/* scrollable body */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">
              {/* Accordion panels */}
              <nav aria-label="Mobile navigation">
                <ul className="divide-y divide-line-200">
                  {groups.map(({ key, data }) => {
                    const isOpen = expanded === key;
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          onClick={() => setExpanded(isOpen ? null : key)}
                          className="flex w-full items-center justify-between gap-4 py-4 text-left"
                        >
                          <span className="text-base font-semibold text-ink-900">
                            {t(data.title, locale) as string}
                          </span>
                          <ChevronDown
                            aria-hidden
                            className={cn(
                              "h-4 w-4 shrink-0 text-ink-500 transition-transform duration-200",
                              isOpen && "rotate-180 text-ink-900",
                            )}
                          />
                        </button>
                        <div
                          className={cn(
                            "grid transition-[grid-template-rows] duration-300 ease-out",
                            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                          )}
                        >
                          <div className="overflow-hidden">
                            <ul className="space-y-1 pb-4">
                              {data.links.map((lnk) => (
                                <li key={lnk.href}>
                                  <Link
                                    href={lnk.href}
                                    onClick={() => setOpen(false)}
                                    className="block rounded-md px-3 py-2.5 text-[15px] text-ink-700 hover:bg-soft hover:text-ink-900"
                                  >
                                    {t(lnk.label, locale) as string}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {/* Contact section */}
              <div className="mt-6 border-t border-line-200 pt-5">
                <p className="kicker">
                  {t(mobileMenuLabels.contactHeading, locale) as string}
                </p>
                <ul className="mt-3 space-y-2 text-sm">
                  <li>
                    <a
                      href={line.href}
                      className="inline-flex items-center gap-2.5 font-semibold text-ink-900 hover:text-brand-600"
                    >
                      <Phone className="h-4 w-4 text-brand-600" aria-hidden />
                      <span className="tabnums">{line.number}</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${company.email}`}
                      className="inline-flex items-center gap-2.5 text-ink-700 hover:text-brand-600 break-all"
                    >
                      <Mail className="h-4 w-4 text-ink-500 shrink-0" aria-hidden />
                      <span>{company.email}</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Language section */}
              <div className="mt-6 border-t border-line-200 pt-5">
                <p className="kicker">
                  {t(mobileMenuLabels.languageHeading, locale) as string}
                </p>
                <div className="mt-3">
                  <LanguageSwitcher native />
                </div>
              </div>
            </div>

            {/* footer CTA */}
            <div className="shrink-0 border-t border-line-200 bg-canvas px-5 py-4">
              <BookingLink
                href="/angebot"
                onClick={() => setOpen(false)}
                className="btn-primary btn-lg w-full"
              >
                {t(commonCopy.fixedPriceCta, locale) as string}
              </BookingLink>
            </div>
          </div>
        </div>,
        document.body,
      )
        : null}
    </>
  );
}

/**
 * Two-line burger that morphs into an X on `open`. Slimmer and more deliberate
 * than the standard 3-line lucide Menu — same hit area, half the "WordPress
 * template" feel.
 */
function BurgerIcon({ open }: { open: boolean }) {
  // Both lines live at y=11 (icon centre). When closed, they translate ±3px
  // apart; when open, they rotate ±45° around their own centre — which is
  // already the icon centre, so the X is symmetric without any offset.
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      aria-hidden
    >
      <line
        x1="4"
        y1="11"
        x2="18"
        y2="11"
        className={cn(
          "origin-center transition-transform duration-200 ease-out",
          open ? "rotate-45" : "-translate-y-[3px]",
        )}
      />
      <line
        x1="4"
        y1="11"
        x2="18"
        y2="11"
        className={cn(
          "origin-center transition-transform duration-200 ease-out",
          open ? "-rotate-45" : "translate-y-[3px]",
        )}
      />
    </svg>
  );
}
